import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { ApiService } from "api/api.service";
import { BoardResource } from "dtos/kanban.dto";
import { RoleTypes } from "dtos/user.dto";
import { ReactElement, useEffect } from "react";
import { FaRegCalendar } from "react-icons/fa";
import { LuWorkflow } from "react-icons/lu";
import {
  TbAppsFilled,
  TbBlockquote,
  TbBriefcase,
  TbBuilding,
  TbCategory,
  TbClipboardCheck,
  TbClipboardList,
  TbCoin,
  TbLayoutKanban,
  TbDashboard,
  TbLayout,
  TbLibraryPhoto,
  TbMail,
  TbMessageStar,
  TbNote,
  TbNotification,
  TbPresentationAnalytics,
  TbTagStarred,
  TbTemplate,
  TbUsers,
  TbUserStar,
  TbMessages,
  TbSubtask,
} from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { storedTokens } from "utils/storedTokens";

type T_MenuItemBase = {
  path: "divider" | "submenu" | `/${string}`;
  icon?: ReactElement;
  label?: string;
  id?: string;
};

type T_MenuItem = T_MenuItemBase & { children?: T_MenuItemBase[] };

const leadPage: T_MenuItemBase = {
  path: "/dashboard",
  icon: <TbDashboard />,
  label: "Dashboard",
};
const casePage: T_MenuItemBase = {
  path: "/dashboard/lead-cases",
  icon: <TbBriefcase />,
  label: "Cases",
};
const assignmentPage: T_MenuItemBase = {
  path: "/dashboard/assignments",
  icon: <TbPresentationAnalytics />,
  label: "Assignments",
};

const notificationPage: T_MenuItem = {
  path: "/dashboard/notifications",
  label: "Notifications",
  icon: <TbNotification />,
};

const contentPages: T_MenuItemBase[] = [
  {
    path: "/dashboard/blogs",
    icon: <TbNote />,
    label: "Blogs",
  },
  {
    path: "/dashboard/categories",
    icon: <TbCategory />,
    label: "Category",
  },
  {
    path: "/dashboard/customer-stories",
    icon: <TbBlockquote />,
    label: "Customer Story",
  },
  {
    path: "/dashboard/testimonials",
    icon: <TbMessageStar />,
    label: "Testimonials",
  },
  {
    path: "/dashboard/gallery",
    icon: <TbLibraryPhoto />,
    label: "Gallery",
  },
];
const affiliatePages: T_MenuItemBase[] = [
  {
    path: "/dashboard/affiliates",
    icon: <TbUserStar />,
    label: "Affiliates",
  },
  {
    path: "/dashboard/affiliate-plans",
    icon: <TbTagStarred />,
    label: "Affiliate Plans",
  },
];

const communicationsPages: T_MenuItemBase[] = [
  {
    path: "/dashboard/communications/conversations",
    icon: <TbMessages />,
    label: "Conversations",
  },
  {
    path: "/dashboard/communications/templates",
    icon: <TbTemplate />,
    label: "Templates",
  },
  {
    path: "/dashboard/emails",
    label: "Inbox",
    icon: <TbMail />,
  },
];

const pipelinePage: T_MenuItemBase = {
  path: "/dashboard/operations/pipelines",
  label: "Pipelines",
  icon: <TbLayoutKanban />,
};
const operationsPages: T_MenuItemBase[] = [
  pipelinePage,
  {
    path: "/dashboard/operations/tasks",
    label: "Tasks",
    icon: <TbSubtask />,
  },
];

const adminPages: T_MenuItem[] = [
  leadPage,
  casePage,
  assignmentPage,
  {
    path: "/dashboard/applications",
    icon: <TbClipboardList />,
    label: "Applications",
  },
  {
    path: "/dashboard/users",
    icon: <TbUsers />,
    label: "Users",
  },
  {
    path: "/dashboard/creditors",
    icon: <TbClipboardCheck />,
    label: "Creditors",
  },
  notificationPage,
  // {
  //   path: "submenu",
  //   label: "Pipeline",
  //   children: KanbanPage,
  // },
  // {
  //   path: "/dashboard/email-template",
  //   label: "Template",
  //   icon: <TbTemplate />,
  // },
  {
    path: "divider",
  },
  {
    path: "submenu",
    label: "Operations",
    children: operationsPages,
  },
  {
    path: "submenu",
    label: "Communications",
    children: communicationsPages,
  },
  {
    path: "submenu",
    label: "Content",
    children: contentPages,
  },
  {
    path: "submenu",
    label: "Affiliate",
    children: affiliatePages,
  },
  {
    path: "divider",
  },
  {
    path: "/dashboard/credits",
    icon: <TbCoin />,
    label: "Credits",
  },
  {
    path: "/dashboard/journey",
    label: "Journey",
    icon: <LuWorkflow />,
  },
  {
    path: "/dashboard/events",
    label: "Events",
    icon: <FaRegCalendar />,
  },
];
const superAdminPages: T_MenuItem[] = [
  ...adminPages,

  {
    path: "/dashboard/organisations",
    icon: <TbBuilding />,
    label: "Organisations",
  },
];
const externalPages: T_MenuItem[] = [];

const negotiatorPages: T_MenuItem[] = [
  {
    path: "/dashboard/applications",
    icon: <TbClipboardList />,
    label: "Applications",
  },
  pipelinePage,
];

export const MENU_ITEMS: Record<RoleTypes, T_MenuItem[]> = {
  [RoleTypes.SUPERADMIN]: superAdminPages,
  [RoleTypes.ADMIN]: adminPages,
  [RoleTypes.AFFILIATE]: [
    {
      path: "/dashboard/affiliate",
      icon: <TbDashboard />,
      label: "Dashboard",
    },
    notificationPage,
  ],
  [RoleTypes.AFFILIATE_ADMIN]: affiliatePages,
  [RoleTypes.AFFILIATE_MANAGER]: [...negotiatorPages, notificationPage],
  [RoleTypes.CASE_ADMIN]: [casePage, assignmentPage, notificationPage],
  [RoleTypes.CASE_MANAGER]: [casePage, notificationPage],
  [RoleTypes.CONTENT_ADMIN]: [...contentPages, notificationPage],
  [RoleTypes.CONTENT_MANAGER]: [...contentPages, notificationPage],
  [RoleTypes.LEAD_ADMIN]: [leadPage, notificationPage],
  [RoleTypes.LEAD_MANAGER]: [leadPage, notificationPage],
  [RoleTypes.USER]: [notificationPage],
  [RoleTypes.ADMIN_READ_ONLY]: adminPages,
  [RoleTypes.DMS_EXTERNAL]: externalPages,
  [RoleTypes.NEGOTIATOR]: [...negotiatorPages, notificationPage],
  [RoleTypes.ISO]: [...negotiatorPages, notificationPage],
};

export const getDefaultPage = (role: RoleTypes) => {
  const items = MENU_ITEMS[role];
  for (const item of items) {
    if (item.path.startsWith("/")) return item.path as `/${string}`;
    for (const child of item.children ?? []) {
      if (child.path.startsWith("/")) return child.path as `/${string}`;
    }
  }

  return null;
};

function makeMenuItem(item: T_MenuItem, i: number, navigate: (path: string) => void): ItemType<MenuItemType> {
  if (item.path === "divider") {
    const obj: ItemType<MenuItemType> = { key: `${item.path}-${i}`, type: "divider" };
    return obj;
  } else if (item.path === "submenu") {
    const menu: ItemType<MenuItemType> = {
      key: `${item.path}-${i}`,
      // icon: item.icon,
      label: item?.label,
      children: item?.children?.map((el, j) => makeMenuItem(el, j, navigate)),
    };
    return menu;
  }
  const menu: ItemType<MenuItemType> = {
    key: `${item.path}`,
    icon: item.icon,
    label: item?.label,
    onClick: () => navigate(item.path),
  };
  return menu;
}

export function useSidebarMenuItems(userRole: RoleTypes | undefined): ItemType<MenuItemType>[] | undefined {
  const navigate = useNavigate();
  const user = ApiService.user.useGetCurrentUser();
  useEffect(() => {
    if (user.data?.organisation?.code) {
      storedTokens.orgCode.setToken(user.data.organisation.code);
    }
  }, [user.data?.organisation?.code]);

  if (!userRole) return undefined;
  return MENU_ITEMS[userRole].map((item, i) => {
    // if (item.path === "submenu") {
    //   // if (item.children === `/dashboard/emails`) {
    //   //   storedTokens.orgCode.setToken(user.data?.organisation?.code || "");
    //   //   item.path = `/dashboard/emails/${user.data?.organisation?.code}`;
    //   // }
    // }
    if (item.path === "submenu") {
      if (item.children) {
        item = {
          ...item,
          children: item.children.map((child) =>
            child.path === "/dashboard/emails"
              ? { ...child, path: `/dashboard/emails/${user.data?.organisation?.code}` }
              : child,
          ),
        };
      }
    }

    return makeMenuItem(item, i, navigate);
  });
}
